import isEmpty from 'lodash-es/isEmpty'
import React from 'react'
import { Loader } from '../Loader'
import * as S from './styled'
import { TableBody } from './TableBody/TableBody'
import type { TableHeadProps } from './TableHead'
import { TableHead } from './TableHead'
import type { TableProps } from './types'

export const Table = <T extends {}>({
  data,
  headers,
  sorting,
  actions,
  sortable = false,
  compact = false,
  rowComponent,
  rowTarget,
  isLoading = false,
  emptyComponent,
  emptyMessage,
  minWidth = 768,
  maxBodyHeight,
  renderFooter,
  getRowLink,
  getRowHref,
  getRowHighlighted = () => false,
  onSortingChange,
  onRowClick,
  onPositionChange,
}: TableProps<T>) => {
  const handleHeadCellClick: TableHeadProps['onHeadCellClick'] = (headerId) => {
    if (!onSortingChange) return

    onSortingChange({
      headerId,
      direction: sorting && sorting.direction === 'desc' ? 'asc' : 'desc',
    })
  }

  if (isEmpty(data)) {
    if (isLoading) {
      return (
        <S.EmptyComponent>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Loader size="large" />
          </div>
        </S.EmptyComponent>
      )
    }

    if (emptyComponent) {
      return <S.EmptyComponent>{emptyComponent}</S.EmptyComponent>
    }

    if (emptyMessage) {
      return <S.EmptyMessage>{emptyMessage}</S.EmptyMessage>
    }

    return null
  }

  return (
    <S.Container role="table" $minWidth={minWidth} $hasActions={!!actions} $sortable={sortable}>
      <TableHead
        headers={headers}
        sorting={sorting}
        sortable={sortable}
        $compact={compact}
        hasActions={!!actions}
        onHeadCellClick={handleHeadCellClick}
      />
      <TableBody
        data={data}
        headers={headers}
        actions={actions}
        sortable={sortable}
        compact={compact}
        maxHeight={maxBodyHeight}
        rowComponent={rowComponent}
        rowTarget={rowTarget}
        isLoading={isLoading}
        renderFooter={renderFooter}
        getRowLink={getRowLink}
        getRowHref={getRowHref}
        getRowHighlighted={getRowHighlighted}
        onRowClick={onRowClick}
        onPositionChange={onPositionChange}
      />
    </S.Container>
  )
}
